import { Pin, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAnchorStore } from '../stores/anchorStore';
import { useChatProvenance, clearChatProvenance } from '../lib/useChatProvenance';
import { useT } from '../i18n';

/**
 * Persistent banner shown between the Topbar and the main content area to
 * signal that the CURRENT page is filtered by the chatbot.
 *
 * Visibility is derived from the URL (`?_chat=1`), not from a store flag, so it
 * behaves exactly like the filter it represents:
 *   - appears when a chat suggestion-pill navigates here (URL gains `_chat=1`);
 *   - disappears the moment the user switches to any other page/section (new
 *     URL, no `_chat`) — the page filters reset at the same time, since both
 *     live in the URL;
 *   - reappears identically when the same pill is clicked again;
 *   - disappears if the user takes over a filter manually (the page strips
 *     `_chat` — see clearChatProvenance).
 * The label text still comes from the anchor store (the originating query).
 */
export function ResearchAnchorBanner() {
  const { isActive, label, clearAnchor } = useAnchorStore();
  const byChat = useChatProvenance();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT();

  // Only surface the banner while the current page is actually chat-filtered.
  if (!byChat || !isActive) return null;

  const handleClear = () => {
    clearAnchor();
    // Also drop the URL flag so the per-page "from chat" chip clears and the
    // banner doesn't immediately re-derive itself as active on the next render.
    clearChatProvenance(setSearchParams, searchParams);
  };

  return (
    <div className="
      flex items-center gap-[8px] px-[24px] h-[36px] shrink-0
      bg-[var(--accent-soft)] border-b border-[var(--accent)]
      text-[var(--accent)] text-[12px]
      animate-[slideDown_150ms_ease-out]
    ">
      <Pin size={12} className="shrink-0" />
      <span className="font-medium shrink-0">{t('anchor.researching')}</span>
      <span className="truncate text-[var(--text-1)] font-semibold">{label}</span>

      <button
        onClick={handleClear}
        className="
          ml-auto shrink-0 flex items-center gap-[4px]
          px-[8px] h-[22px] rounded-[var(--r1)]
          text-[var(--text-2)] hover:text-[var(--text-1)]
          hover:bg-[var(--surface-2)] transition-colors
        "
      >
        <X size={11} />
        <span className="text-[11px]">{t('anchor.clear')}</span>
      </button>
    </div>
  );
}
